/**
 * API Service
 * @description Core API service for making HTTP requests to ObservePro backend
 * 
 * Note: This uses direct HTTP calls to the backend API.
 * Since we deploy static build assets, we cannot use Next.js API routes.
 */

import { env } from '@/config/environment';
import { isTokenExpired } from '@/utils/jwtUtils';

const CORE_API_BASE_URL = process.env.NEXT_PUBLIC_API_BASE_URL!;

export class ApiError extends Error {
  constructor(
    message: string,
    public status?: number,
    public data?: unknown
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

interface RequestOptions extends RequestInit {
  params?: Record<string, unknown>;
}

/**
 * Core fetch wrapper with error handling
 */
function buildUrl(baseUrl: string, endpoint: string, params?: Record<string, unknown>): string {
  let url = `${baseUrl}${endpoint}`;
  if (params) {
    const searchParams = new URLSearchParams();
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        searchParams.append(key, String(value));
      }
    });
    const queryString = searchParams.toString();
    if (queryString) {
      url += `?${queryString}`;
    }
  }
  return url;
}

function requestWithBase(baseUrl: string) {
  return async function request<T>(
    endpoint: string,
    options: RequestOptions = {}
  ): Promise<T> {
    const { params, ...fetchOptions } = options;

    const url = buildUrl(baseUrl, endpoint, params);

    // Build headers object
    const headersObj: Record<string, string> = {
      'Content-Type': 'application/json',
      'ngrok-skip-browser-warning': 'true',
    };

    // Merge existing headers if they're a plain object
    if (fetchOptions.headers) {
      if (fetchOptions.headers instanceof Headers) {
        fetchOptions.headers.forEach((value, key) => {
          headersObj[key] = value;
        });
      } else if (Array.isArray(fetchOptions.headers)) {
        fetchOptions.headers.forEach(([key, value]) => {
          headersObj[key] = value;
        });
      } else {
        Object.assign(headersObj, fetchOptions.headers);
      }
    }

    // Add authorization token if available
    if (typeof window !== 'undefined') {
      const storedToken = localStorage.getItem(env.JWT_STORAGE_KEY);
      if (storedToken && !isTokenExpired(storedToken)) {
        headersObj.Authorization = `Bearer ${storedToken}`;
      }
    }

    const headers: HeadersInit = headersObj;

    try {
      const response = await fetch(url, {
        ...fetchOptions,
        headers,
      });

      if (!response.ok) {
        // Try JSON first; if not JSON, fall back to text
        let errorData: unknown = undefined;
        try {
          errorData = await response.json();
        } catch {
          try {
            const text = await response.text();
            errorData = { message: text } as { message?: string };
          } catch {
            errorData = {};
          }
        }

        const fallbackMessage = `HTTP ${response.status}: ${response.statusText}`;
        let message = fallbackMessage;
        if (errorData && typeof errorData === 'object') {
          const errorObj = errorData as Record<string, unknown>;
          // Check for 'message' field first, then 'detail' field
          if ('message' in errorObj && typeof errorObj.message === 'string') {
            message = errorObj.message;
          } else if ('detail' in errorObj && typeof errorObj.detail === 'string') {
            message = errorObj.detail;
          }
        }
        throw new ApiError(message, response.status, errorData);
      }

      // Parse JSON response
      const data = await response.json();
      return data as T;
    } catch (error) {
      if (error instanceof ApiError) {
        throw error;
      }
      throw new ApiError(error instanceof Error ? error.message : 'An unknown error occurred');
    }
  };
}

export function createApiClient(baseUrl: string) {
  const request = requestWithBase(baseUrl);
  return {
    get: <T>(endpoint: string, params?: Record<string, unknown>, init?: RequestInit) =>
      request<T>(endpoint, { method: 'GET', params, ...init }),
    post: <T>(endpoint: string, data?: unknown, params?: Record<string, unknown>, init?: RequestInit) =>
      request<T>(endpoint, { method: 'POST', body: data ? JSON.stringify(data) : undefined, params, ...init }),
    put: <T>(endpoint: string, data?: unknown, params?: Record<string, unknown>) =>
      request<T>(endpoint, { method: 'PUT', body: data ? JSON.stringify(data) : undefined, params }),
    delete: <T>(endpoint: string, params?: Record<string, unknown>) =>
      request<T>(endpoint, { method: 'DELETE', params }),
    patch: <T>(endpoint: string, data?: unknown, params?: Record<string, unknown>) =>
      request<T>(endpoint, { method: 'PATCH', body: data ? JSON.stringify(data) : undefined, params }),
  };
}

/**
 * API methods
 */
// Core API client (default)
export const api = {
  // Keep convenience endpoints specific to core API
  health: () => requestWithBase(CORE_API_BASE_URL)('/health'),
  stats: () => requestWithBase(CORE_API_BASE_URL)('/stats'),
  ...createApiClient(CORE_API_BASE_URL),
};

// Scrapper API client (separate service)
const SCRAPPER_API_BASE_URL = process.env.NEXT_PUBLIC_SCRAPPER_API_BASE_URL!;
export const scrapperApi = createApiClient(SCRAPPER_API_BASE_URL);

// Log Analyzer API client (separate service)
// Default to staging URL
let currentLogAnalyzerBaseUrl = env.LOG_ANALYZER_API_BASE_URL || process.env.NEXT_PUBLIC_LOG_ANALYZER_API_BASE_URL || 'https://cf1a51957858.ngrok-free.app';

// Check for saved environment preference in localStorage
if (typeof window !== 'undefined') {
  const savedEnv = localStorage.getItem(env.LOG_ANALYZER_ENV_KEY || 'log_analyzer_environment');
  if (savedEnv === 'production' && env.LOG_ANALYZER_API_BASE_URL_PROD) {
    currentLogAnalyzerBaseUrl = env.LOG_ANALYZER_API_BASE_URL_PROD;
  }
}

if (!currentLogAnalyzerBaseUrl) {
  console.warn('Warning: NEXT_PUBLIC_LOG_ANALYZER_API_BASE_URL is not configured. Log Analyzer API calls will fail.');
}

// Create mutable logAnalyzerApi
let logAnalyzerApiInstance = createApiClient(currentLogAnalyzerBaseUrl);

export const logAnalyzerApi = {
  get: <T>(...args: Parameters<ReturnType<typeof createApiClient>['get']>) => logAnalyzerApiInstance.get<T>(...args),
  post: <T>(...args: Parameters<ReturnType<typeof createApiClient>['post']>) => logAnalyzerApiInstance.post<T>(...args),
  put: <T>(...args: Parameters<ReturnType<typeof createApiClient>['put']>) => logAnalyzerApiInstance.put<T>(...args),
  delete: <T>(...args: Parameters<ReturnType<typeof createApiClient>['delete']>) => logAnalyzerApiInstance.delete<T>(...args),
};

/**
 * Switch Log Analyzer API environment
 * @param environment - 'staging' or 'production'
 */
export const switchLogAnalyzerEnvironment = (environment: 'staging' | 'production'): void => {
  const newBaseUrl = environment === 'production' 
    ? (env.LOG_ANALYZER_API_BASE_URL_PROD || env.LOG_ANALYZER_API_BASE_URL || process.env.NEXT_PUBLIC_LOG_ANALYZER_API_BASE_URL || 'https://cf1a51857858.ngrok-free.app')
    : (env.LOG_ANALYZER_API_BASE_URL || process.env.NEXT_PUBLIC_LOG_ANALYZER_API_BASE_URL || 'https://cf1a51857858.ngrok-free.app');
  
  logAnalyzerApiInstance = createApiClient(newBaseUrl);
  
  // Save preference to localStorage
  if (typeof window !== 'undefined') {
    localStorage.setItem(env.LOG_ANALYZER_ENV_KEY || 'log_analyzer_environment', environment);
  }
};

export default api;

